// ANSI terminal escape handling for deployment logs.
//
// Tools the deployer shells out to — tofu/terraform above all — colorize their
// stdout with SGR sequences (`ESC[1m` bold, `ESC[31m` red, `ESC[0m` reset). A
// browser shows those as literal `[0m [1m…` noise, so a line is split into
// styled segments here and the logs view renders them as spans. Non-SGR CSI
// sequences (cursor movement, erase-line) carry nothing visible and are dropped.
//
// A bare `[<digits>m` with its ESC byte lost somewhere upstream is treated as
// SGR too — real log text essentially never contains that shape, and the lost
// ESC is exactly what turns colored output into the garbage this exists to fix.

export type AnsiColor =
  | 'black'
  | 'red'
  | 'green'
  | 'yellow'
  | 'blue'
  | 'magenta'
  | 'cyan'
  | 'white';

export interface AnsiSegment {
  text: string;
  bold?: boolean;
  dim?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: AnsiColor;
}

const COLORS: AnsiColor[] = ['black', 'red', 'green', 'yellow', 'blue', 'magenta', 'cyan', 'white'];

// ESC-prefixed CSI of any final letter, or an orphaned (ESC-less) SGR.
// Matching the ESC control byte is the whole point here.
// eslint-disable-next-line no-control-regex
const ESCAPE_RE = /\x1b\[([0-9;]*)([A-Za-z])|\[([0-9;]*)m/g;

type Style = Omit<AnsiSegment, 'text'>;

const applySgr = (style: Style, params: string): Style => {
  // `ESC[m` is shorthand for reset.
  const codes = params === '' ? [0] : params.split(';').map(Number);
  let next: Style = { ...style };
  for (let i = 0; i < codes.length; i++) {
    const code = codes[i];
    if (code === 0) next = {};
    else if (code === 1) next.bold = true;
    else if (code === 2) next.dim = true;
    else if (code === 3) next.italic = true;
    else if (code === 4) next.underline = true;
    else if (code === 22) {
      delete next.bold;
      delete next.dim;
    } else if (code === 23) delete next.italic;
    else if (code === 24) delete next.underline;
    else if (code >= 30 && code <= 37) next.color = COLORS[code - 30];
    else if (code >= 90 && code <= 97) next.color = COLORS[code - 90];
    else if (code === 39) delete next.color;
    // 256-color / truecolor (38;5;n, 38;2;r;g;b): skip the operands; no palette
    // mapping is worth it for what tools actually emit here.
    else if (code === 38 || code === 48) i += codes[i + 1] === 5 ? 2 : 4;
    // Backgrounds (40–47, 100–107, 49) and anything else: ignored.
  }
  return next;
};

/** Whether `text` holds any escape sequence this module understands. */
export const hasAnsi = (text: string): boolean => new RegExp(ESCAPE_RE.source).test(text);

/** `text` with every escape sequence removed. */
export const stripAnsi = (text: string): string => text.replace(ESCAPE_RE, '');

/**
 * Split `text` into runs of uniformly styled text. Empty runs are dropped, so
 * a line that is only escapes yields `[]`; adjacent runs with the same style
 * are not merged (the renderer doesn't care).
 */
export const parseAnsi = (text: string): AnsiSegment[] => {
  const out: AnsiSegment[] = [];
  let style: Style = {};
  let last = 0;
  const re = new RegExp(ESCAPE_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), ...style });
    // Group 1/2: ESC-prefixed CSI (only `m` styles); group 3: orphaned SGR.
    if (m[2] === 'm') style = applySgr(style, m[1]);
    else if (m[3] !== undefined) style = applySgr(style, m[3]);
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ text: text.slice(last), ...style });
  return out;
};
